// src/components/dialogs/ChangePasswordDialog.tsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate, useGetIdentity } from 'react-admin';
import { API_CONFIG } from '@/config/constants';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  userName?: string;
  isCurrentUser?: boolean; // Nuevo prop opcional para indicar explícitamente si es el usuario actual
}

interface PasswordValidation {
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

/**
 * ChangePasswordDialog - Componente reutilizable para cambiar contraseña
 * 
 * Casos de uso:
 * 1. Usuario cambiando SU PROPIA contraseña: Llama a POST /api/auth/change-password (requiere contraseña actual)
 * 2. Admin reseteando contraseña de OTRO usuario: Llama a POST /api/users/{id}/reset-password (NO requiere contraseña actual)
 * 3. Forgot password (futuro): Usará un endpoint específico con token de recuperación
 */
const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
  isCurrentUser
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresCurrentPassword, setRequiresCurrentPassword] = useState(true);

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  // Determinar si necesita contraseña actual
  useEffect(() => {
    // Si se especifica explícitamente que es el usuario actual
    if (isCurrentUser === true) {
      setRequiresCurrentPassword(true);
      return;
    }
    
    // Si se especifica explícitamente que NO es el usuario actual
    if (isCurrentUser === false) {
      setRequiresCurrentPassword(false);
      return;
    }
    
    // Lógica automática basada en identity y userId
    if (identity && userId) {
      const isChangingOwnPassword = (String(userId) === String(identity.id));
      setRequiresCurrentPassword(isChangingOwnPassword);
    } else if (!userId) {
      // Si no hay userId, asumimos que es el usuario actual
      setRequiresCurrentPassword(true);
    }
  }, [identity, userId, isCurrentUser]);

  // Validación de requisitos de contraseña
  const validatePassword = (password: string): PasswordValidation => ({
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[@$!%*?&]/.test(password),
  });

  const passwordValidation = validatePassword(newPassword);
  const isPasswordValid = Object.values(passwordValidation).every(v => v);
  const passwordsMatch = newPassword === confirmPassword && newPassword !== '';

  const handleClose = () => {
    if (!loading) {
      // Limpiar el formulario
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validaciones
    if (requiresCurrentPassword && !currentPassword) {
      setError('La contraseña actual es requerida');
      return;
    }

    if (!isPasswordValid) {
      setError('La nueva contraseña no cumple con los requisitos');
      return;
    }

    if (!passwordsMatch) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (requiresCurrentPassword && currentPassword === newPassword) {
      setError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);

    try {
      if (requiresCurrentPassword) {
        // CASO 1: Usuario cambiando SU PROPIA contraseña
        // Llamar directamente al endpoint /api/auth/change-password
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/change-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            currentPassword,
            newPassword
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
        }
      } else {
        // CASO 2: Admin reseteando contraseña de OTRO usuario
        // Usar el endpoint /api/users/{id}/reset-password a través del dataProvider
        await dataProvider.create(`users/${userId}/reset-password`, {
          data: {
            newPassword: newPassword
          }
        });
      }

      notify('Contraseña actualizada correctamente', { type: 'success' });
      handleClose();
    } catch (err: any) {
      const errorMessage = err?.body?.error || err?.message || 'Error al cambiar la contraseña';
      
      // Manejar errores específicos del backend
      if (errorMessage.includes('incorrect') || errorMessage.includes('invalid')) {
        setError('La contraseña actual es incorrecta');
      } else if (errorMessage.includes('forbidden') || errorMessage.includes('Cannot reset')) {
        setError('No tienes permisos para resetear la contraseña de este usuario');
      } else if (errorMessage.includes('equal or higher role')) {
        setError('No puedes resetear la contraseña de usuarios con rol igual o superior al tuyo');
      } else if (errorMessage.includes('outside your')) {
        setError('Solo puedes resetear contraseñas de usuarios en tu misma organización');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const RequirementItem: React.FC<{ met: boolean; text: string }> = ({ met, text }) => (
    <ListItem dense sx={{ py: 0 }}>
      <ListItemIcon sx={{ minWidth: 30 }}>
        {met ? (
          <CheckIcon color="success" fontSize="small" />
        ) : (
          <CloseIcon color="error" fontSize="small" />
        )}
      </ListItemIcon>
      <ListItemText 
        primary={text} 
        primaryTypographyProps={{ 
          variant: 'caption',
          color: met ? 'success.main' : 'text.secondary'
        }}
      />
    </ListItem>
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon />
        {requiresCurrentPassword 
          ? 'Cambiar Contraseña' 
          : `Resetear Contraseña de ${userName}`}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Contraseña Actual (solo si es necesaria) */}
          {requiresCurrentPassword && (
            <TextField
              autoFocus
              label="Contraseña Actual"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {/* Nueva Contraseña */}
          <TextField
            label="Nueva Contraseña"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            fullWidth
            required
            autoFocus={!requiresCurrentPassword}
            error={newPassword !== '' && !isPasswordValid}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Requisitos de contraseña */}
          {newPassword && (
            <Box sx={{ bgcolor: 'background.default', p: 1, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                La contraseña debe tener:
              </Typography>
              <List dense sx={{ py: 0 }}>
                <RequirementItem 
                  met={passwordValidation.minLength} 
                  text="Al menos 8 caracteres" 
                />
                <RequirementItem 
                  met={passwordValidation.hasUpperCase} 
                  text="Una letra mayúscula" 
                />
                <RequirementItem 
                  met={passwordValidation.hasLowerCase} 
                  text="Una letra minúscula" 
                />
                <RequirementItem 
                  met={passwordValidation.hasNumber} 
                  text="Un número" 
                />
                <RequirementItem 
                  met={passwordValidation.hasSpecialChar} 
                  text="Un carácter especial (@$!%*?&)" 
                />
              </List>
            </Box>
          )}

          {/* Confirmar Contraseña */}
          <TextField
            label="Confirmar Nueva Contraseña"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            fullWidth
            required
            error={confirmPassword !== '' && !passwordsMatch}
            helperText={confirmPassword !== '' && !passwordsMatch ? 'Las contraseñas no coinciden' : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading || 
            (requiresCurrentPassword && !currentPassword) || 
            !isPasswordValid || 
            !passwordsMatch
          }
          startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
        >
          {loading 
            ? 'Procesando...' 
            : requiresCurrentPassword 
              ? 'Cambiar Contraseña'
              : 'Resetear Contraseña'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;